import { useUserQuizes } from '@/module/profile/user/hooks/useFetchUserQuizes';
import { useSearchQuizQuery } from '@/shared/quizList/hooks/useSearchQuizQuery';
import {
  type LoadedSectionView,
  resolveLoadedSectionView,
} from '@/shared/utils/resolveSectionView';

import { useUserFolderPreview } from './useUserFolderPreview';

interface UserProfileLayout {
  folders: boolean;
  quizzes: boolean;
}

/**
 * 조합 → 그릴 것. 내 퀴즈와 두 가지가 다르다.
 *
 * - 전용 빈 화면이 없다. 둘 다 비면 퀴즈 섹션을 그려 그 목록의 빈 상태(공개 퀴즈 없음)를 보인다 — 지금 프로필과 같다.
 * - 로딩 중(`pending`)이 없다. 퀴즈 목록은 SSR 로 그려져 있어, 폴더를 기다리느라 숨기면 하이드레이션이 서버
 *   HTML 과 어긋난다. 그래서 다 받은 뒤의 조합(`resolveLoadedSectionView`)만 쓴다.
 */
const LAYOUT: Record<LoadedSectionView, UserProfileLayout> = {
  both: { folders: true, quizzes: true },
  foldersOnly: { folders: true, quizzes: false },
  quizzesOnly: { folders: false, quizzes: true },
  empty: { folders: false, quizzes: true },
};

/**
 * 남의 프로필에서 폴더·퀴즈 섹션 중 무엇을 그릴지 정한다.
 *
 * 공개 폴더가 없으면 폴더 섹션은 제목째 사라진다(`새 폴더` 카드가 없으므로). 검색 결과가 폴더에만 있으면 퀴즈 섹션을
 * 숨긴다. 아직 받는 중인 쪽은 있다고 본다 — 받기도 전에 섹션을 숨겼다 다시 그리면 화면이 출렁인다.
 */
export const useUserProfileLayout = (userId: string): UserProfileLayout => {
  const { searchText } = useSearchQuizQuery();
  const folder = useUserFolderPreview(userId);
  const quiz = useUserQuizes(userId);

  return LAYOUT[
    resolveLoadedSectionView({
      isSearching: !!searchText,
      hasFolders: !folder.isError && folder.totalCount > 0,
      hasQuizzes: quiz.isLoading || quiz.totalCount > 0,
      hasCreateCard: false,
    })
  ];
};
